import React from 'react';
import styled from 'styled-components';
import PuzzlePieceBackground from '../Assets/TIM/puzzlePieceBackground.png';
import { Trans, useTranslation } from 'react-i18next';
import { Close } from '../Common/Svg/Icons';
import PoweredByKindred from './shared/PoweredByKindred';
import {
  ANALYTICS_PAGE_NAMES,
  trackClickedActionEvent,
} from '../Common/Analytics/utils';
import { usePageViewTracking, useQuery } from '../Common/Hooks';
import { isIOS, isAndroid } from 'react-device-detect';
import { useHistory } from 'react-router-dom';
import { Path } from '../Routing';
import { onClose } from './KindredAndroid';
import i18n from 'i18next';
import { accessibilityFocusStyleLight } from '../Theming';
import { Helmet } from 'react-helmet';
import timIcon from '../Assets/PartnerNewpluginsdkActivate/tim-ios-logo.png';
import { sendDataToiOS } from './KindredIOS';
import TimTopNavigation from './TimTopNavigation';
import '../Theming/Fonts/custom-fonts.css';

const Container = styled.div`
  background-color: #0164f2;
  display: flex;
  flex-direction: column;
  flex: 1;
  height: ${window.innerHeight + 60}px;
  position: relative;
  margin-top: 60px;
  font-family: TIMSans-Regular;
`;

const ImageContainer = styled.div`
  position: absolute;
  display: flex;
  width: 100%;
  flex: 1;
  align-items: center;
  justify-content: center;
`;

const Image = styled.img`
  width: 320px;
  z-index: 1;
  margin-top: -95px;

  @media (max-height: 600px) {
    width: 290px;
  }
`;

const MainContent = styled.div`
  margin-top: 130px;

  @media (max-height: 600px) {
    margin-top: 100px;
  }
`;

const TitleText = styled.p`
  margin: 0;
  padding: 0px 16px 0 16px;
  color: #fff;
  font-size: 20px;
  line-height: normal;
  font-weight: bold;
  text-align: center;
`;

const Description = styled.p`
  margin: 0;
  padding: 8px 20px 0 20px;
  color: #fff;
  text-align: center;
  font-size: 16px;
  line-height: normal;
`;

const KindredDisclaimerContainer = styled.div`
  margin-top: 20px;

  @media (max-height: 600px) {
    margin-top: 10px;
  }
`;

const KindredDisclaimerContainerText = styled.p`
  margin: 10px 0 0 0;
  padding: 0 16px 0 16px;
  font-size: 11px;
  color: #fff;
  text-align: center;
  line-height: normal;
`;

const Footer = styled.footer`
  display: flex;
  flex-direction: column;
  padding-inline: 16px;
  padding-bottom: 20px;
  position: fixed;
  left: 0;
  bottom: 0;
  width: 100%;

  @media (max-height: 600px) {
    padding-bottom: 10px;
  }
`;

const Button = styled.button`
  height: 52px;
  background-color: #fff;
  border: none;
  margin-top: 16px;
  color: #fff;
  font-size: 16px;
  font-weight: bold;
  color: #0164f2;
  font-family: TIMSans-Regular;
`;

const PolicyDisclaimer = styled.p`
  font-size: 11px;
  text-align: center;
  color: #fff;
  margin: 20px 0 0 0;
  padding: 0;
  line-height: normal;

  @media (max-height: 600px) {
    margin: 5px 0 0 0;
  }
`;

const LinkText = styled.a`
  font-size: 11px;
  color: #fff;
  text-decoration: underline;
  cursor: pointer;
  text-align: center;

  ${accessibilityFocusStyleLight}
`;

const FaqLink = styled.a`
  font-size: 18px;
  color: #fff;
  text-decoration: underline;
  cursor: pointer;
  text-align: center;

  ${accessibilityFocusStyleLight}
`;

const TIMActivationHome = () => {
  if (i18n.language != 'it') {
    i18n.changeLanguage('it');
  }
  const { t } = useTranslation();
  const history = useHistory();

  usePageViewTracking(ANALYTICS_PAGE_NAMES.extension_explainer, 'TIM');
  const query = useQuery();
  /**
   * TODO: Confirm and use logic to determine the activation message
   */
  const onActivateClick = () => {
    trackClickedActionEvent(
      query,
      'TIM',
      'activate',
      ANALYTICS_PAGE_NAMES.extension_explainer
    );

    if (isIOS) {
      const link = new URL(window.location.href);
      link.pathname = Path.TimPluginActivationActivatePlugin;

      sendDataToiOS({
        message: 'copyAndGo',
        payload: {
          site: link.toString(),
          couponCode: '',
        },
      });
      if (window?.history?.length > 1) {
        window?.history?.back();
      }
    } else {
      history.push({
        pathname: Path.TIMAndroidActivationHome,
        search: location.search,
      });
    }
  };

  const getButtonText = () => {
    if (isAndroid) {
      return t('tim.activateChromeExtension');
    } else {
      return t('tim.activateSafariExtension');
    }
  };

  const getDescriptionText = () => {
    if (isAndroid) {
      return t('tim.activateExtensionDescriptionChrome');
    } else {
      return t('tim.activateExtensionDescriptionSafari');
    }
  };

  const onFAQClick = () => {
    trackClickedActionEvent(
      query,
      'TIM',
      'faq',
      ANALYTICS_PAGE_NAMES.extension_explainer
    );
  };

  return (
    <div>
      <TimTopNavigation />
      <Container>
        <Helmet>
          <link rel="icon" type="image/png" sizes="16x16" href={timIcon} />
          <title>TIM Benefit</title>
        </Helmet>
        <ImageContainer>
          <Image src={PuzzlePieceBackground} alt="discounts puzzle piece" />
        </ImageContainer>

        <MainContent>
          <TitleText>{t('tim.activateExtension')}</TitleText>
          <Description>{getDescriptionText()}</Description>

          <KindredDisclaimerContainer>
            <PoweredByKindred />
            <KindredDisclaimerContainerText>
              {t('tim.disclaimer')}
            </KindredDisclaimerContainerText>
          </KindredDisclaimerContainer>
        </MainContent>

        <Footer>
          <FaqLink href="https://tim.kindred.co/faq" onClick={onFAQClick}>
            {t('tim.seeFaq')}
          </FaqLink>
          <PolicyDisclaimer>
            <Trans i18nKey={'tim.policyDisclaimer'}>
              <LinkText href="https://tim.kindred.co/privacy" tabIndex={0}>
                informativa privacy
              </LinkText>
              <LinkText
                href="https://tim.kindred.co/terms-and-conditions"
                tabIndex={0}
              >
                termini e condizioni
              </LinkText>
            </Trans>
          </PolicyDisclaimer>
          <Button onClick={onActivateClick}>
            {getButtonText().toLocaleUpperCase()}
          </Button>
        </Footer>
      </Container>
    </div>
  );
};

export default TIMActivationHome;
